import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Send, Plus, Minus, Trash2, User, MapPin, ClipboardList, Loader2, ArrowLeft, Info, ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { whatsappNumber } from '../data/mock';
import { Helmet } from 'react-helmet-async';
import AOS from 'aos';
import logo from '../assets/logo3.png';
import pricelistImg from '../assets/pricelist.jpeg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VACUUM_PRICE = 2500;
const REGION_API = 'https://www.emsifa.com/api-wilayah-indonesia/api';

const OrderPage = () => {
  const { language, t } = useLanguage();
  const { cart, handleAddToCart, handleIncreaseQuantity, handleDecreaseQuantity, handleRemoveFromCart, handleClearCart, getTotalItems } = useCart();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isPricelistOpen, setIsPricelistOpen] = useState(false);
  
  // State for expanded list (mobile)
  const [expandedProductId, setExpandedProductId] = useState(null);

  // Region Data State
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [regionLoading, setRegionLoading] = useState({
    provinces: false,
    cities: false,
    districts: false
  });

  // Customer Information State
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    province: '',
    provinceId: '',
    city: '',
    cityId: '',
    district: '',
    districtId: '',
    postalCode: '',
    addressStreet: '',
    addressDetails: '',
    useVacuum: false
  });

  // Refs for auto-scroll
  const categoryRefs = {
    premium: useRef(null),
    basah: useRef(null),
    kering: useRef(null)
  };

  useEffect(() => {
    fetchProducts();
    fetchProvinces();
    window.scrollTo(0, 0);
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
      // Refresh AOS to detect new elements
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProvinces = async () => {
    setRegionLoading(prev => ({ ...prev, provinces: true }));
    try {
      const response = await axios.get(`${REGION_API}/provinces.json`);
      setProvinces(response.data);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    } finally {
      setRegionLoading(prev => ({ ...prev, provinces: false }));
    }
  };

  const fetchCities = async (provinceId) => {
    setRegionLoading(prev => ({ ...prev, cities: true }));
    try {
      const response = await axios.get(`${REGION_API}/regencies/${provinceId}.json`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setRegionLoading(prev => ({ ...prev, cities: false }));
    }
  };

  const fetchDistricts = async (cityId) => {
    setRegionLoading(prev => ({ ...prev, districts: true }));
    try {
      const response = await axios.get(`${REGION_API}/districts/${cityId}.json`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    } finally {
      setRegionLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const handleProvinceChange = (provinceId) => {
    const province = provinces.find(p => p.id === provinceId);
    setCustomerInfo(prev => ({
      ...prev,
      provinceId: provinceId,
      province: province ? province.name : '',
      city: '',
      cityId: '',
      district: '',
      districtId: ''
    }));
    setCities([]);
    setDistricts([]);
    fetchCities(provinceId);
  };

  const handleCityChange = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    setCustomerInfo(prev => ({
      ...prev,
      cityId: cityId,
      city: city ? city.name : '',
      district: '',
      districtId: ''
    }));
    setDistricts([]);
    fetchDistricts(cityId);
  };

  const handleDistrictChange = (districtId) => {
    const district = districts.find(d => d.id === districtId);
    setCustomerInfo(prev => ({
      ...prev,
      districtId: districtId,
      district: district ? district.name : ''
    }));
  };

  const getTotalPrice = () => {
    const productsTotal = Object.keys(cart).reduce((total, productId) => {
      const product = products.find((p) => p.id === productId);
      return total + (product ? product.price * cart[productId] : 0);
    }, 0);
    const vacuumTotal = customerInfo.useVacuum ? getTotalItems() * VACUUM_PRICE : 0;
    return productsTotal + vacuumTotal;
  };

  const validateForm = () => {
    const { fullName, province, city, district, postalCode, addressStreet } = customerInfo;
    if (!fullName || !province || !city || !district || !postalCode || !addressStreet) {
      toast.error(t.order.errorIncomplete);
      return false;
    }
    return true;
  };

  const handleSendToWhatsApp = () => {
    if (Object.keys(cart).length === 0) {
      toast.error(t.order.errorEmpty);
      return;
    }
    if (!validateForm()) return;

    let message = '*PESANAN GRIYA BAKPIA*\n\n';

    message += '*DATA PEMESAN*\n';
    message += `Nama: ${customerInfo.fullName}\n\n`;

    message += '*ALAMAT PENGIRIMAN*\n';
    message += `${customerInfo.addressStreet}\n`;
    message += `${customerInfo.district}, ${customerInfo.city}\n`;

    if (customerInfo.addressDetails) {
        message += `${customerInfo.province}, ${customerInfo.postalCode}\n`;
        message += `Detail: ${customerInfo.addressDetails}\n\n`;
    } else {
        message += `${customerInfo.province}, ${customerInfo.postalCode}\n\n`;
    }
    message += '*DAFTAR PESANAN*\n';
    const totalItemsCount = getTotalItems();
    Object.keys(cart).forEach((productId) => {
      const product = products.find((p) => p.id === productId);
      if (product) {
        const quantity = cart[productId];
        const subtotal = product.price * quantity;
        message += `• ${product.name} (isi ${product.pieces_per_box || 10})\n`;
        message += `  ${quantity} box x Rp ${product.price.toLocaleString()} = Rp ${subtotal.toLocaleString()}\n\n`;
      }
    });

    if (customerInfo.useVacuum) {
      const vacuumTotal = totalItemsCount * VACUUM_PRICE;
      message += `*TAMBAHAN*\n`;
      message += `• Layanan Vacuum\n`;
      message += `  ${totalItemsCount} box x Rp ${VACUUM_PRICE.toLocaleString()} = Rp ${vacuumTotal.toLocaleString()}\n\n`;
    }

    message += `*TOTAL PEMBAYARAN: Rp ${getTotalPrice().toLocaleString()}*\n\n`;
    message += 'Saya ingin memesan produk di atas. Terima kasih!';

    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
    handleClearCart();
    setIsCheckoutModalOpen(false);
    toast.success(t.order.successMsg);
  };

  const onResetCart = () => {
    if (window.confirm(t.order.confirmClear)) {
      handleClearCart();
      setIsCheckoutModalOpen(false);
      toast.info(t.order.cartCleared);
    }
  };

  const scrollToCategory = (cat) => {
    categoryRefs[cat].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleExpand = (id) => {
    setExpandedProductId(expandedProductId === id ? null : id);
  };

  const renderProductSection = (category, title) => {
    const filtered = products.filter(p => p.category === category || (!p.category && category === 'basah'));
    if (filtered.length === 0) return null;

    return (
      <>
        <Helmet>
          <link rel="canonical" href="https://griyabakpia.com/order" />
        </Helmet>

        <div key={category} ref={categoryRefs[category]} className="mb-12 pt-4">
          <h2 className="text-2xl font-bold text-[#3E2723] mb-6 flex items-center gap-2">
            <div className="w-2 h-8 bg-[#B22222] rounded-full"></div>
            {title}
          </h2>
          
          {/* Desktop View: Grid */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((product, index) => (
              <Card 
                key={product.id} 
                className="overflow-hidden border-none shadow-md hover:shadow-lg transition-all flex flex-col"
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="relative h-48">
                  <img
                    src={product.image_url ? `${BACKEND_URL}${product.image_url}` : product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-[#B22222] text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                    Rp {product.price.toLocaleString()}
                  </div>
                  {cart[product.id] > 0 && (
                    <div className="absolute top-3 left-3 bg-white text-[#B22222] px-3 py-1 rounded-full font-bold shadow-md flex items-center gap-1">
                      <ShoppingCart size={14} />
                      <span>{cart[product.id]}</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-4 flex flex-col flex-1">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-[#3E2723] leading-tight mb-1">
                      {product.name} ({t.order.isi} {product.pieces_per_box || 10})
                    </h3>
                    <p className="text-xs text-gray-600 line-clamp-3 mb-4">
                      {product.description || `Varian Bakpia ${product.name} dengan kualitas premium dan rasa yang otentik.`}
                    </p>
                  </div>
                  <Button 
                    onClick={() => handleAddToCart(product, language)}
                    className="w-full bg-[#B22222] hover:bg-[#8B0000] text-white rounded-full h-9"
                  >
                    <Plus size={16} className="mr-1" /> Tambah
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile View: List */}
          <div className="flex flex-col gap-3 sm:hidden">
            {filtered.map((product, index) => (
              <Card 
                key={product.id} 
                className="overflow-hidden border-none shadow-sm"
                data-aos="fade-up"
                data-aos-delay={index * 30}
              >
                <CardContent className="p-0">
                  <div className="flex items-center p-3 gap-3">
                    {/* Small Photo */}
                    <div 
                      className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
                      onClick={() => toggleExpand(product.id)}
                    >
                      <img
                        src={product.image_url ? `${BACKEND_URL}${product.image_url}` : product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" onClick={() => toggleExpand(product.id)}>
                      <h3 className="font-bold text-[#3E2723] text-sm truncate">
                        {product.name} ({t.order.isi} {product.pieces_per_box || 10})
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="font-bold text-[#B22222] text-sm">Rp {product.price.toLocaleString()}</p>
                        <span className="text-[10px] text-gray-400">/box</span>
                      </div>
                      <div className="text-[10px] text-blue-600 font-medium flex items-center mt-1">
                        <Info size={10} className="mr-0.5" /> {expandedProductId === product.id ? t.order.tutupDetail : t.order.lihatDetail}
                      </div>
                    </div>

                    {/* Add Button */}
                    <div className="flex flex-col items-end gap-2">
                      {cart[product.id] > 0 && (
                        <Badge variant="secondary" className="bg-[#B22222]/10 text-[#B22222] text-[10px] py-0 px-2 h-5 border-none">
                          {cart[product.id]}
                        </Badge>
                      )}
                      <Button 
                        onClick={() => handleAddToCart(product, language)}
                        className="bg-[#B22222] hover:bg-[#8B0000] text-white rounded-xl px-3 h-9 text-xs font-bold"
                      >
                        <ShoppingCart size={14} className="mr-1.5 text-white" /> + Tambah
                      </Button>
                    </div>
                  </div>

                  {/* Accordion Content */}
                  {expandedProductId === product.id && (
                    <div className="px-3 pb-3 pt-0 animate-in slide-in-from-top-2 duration-200">
                      <div className="bg-gray-50 rounded-lg p-2.5 text-xs text-gray-600 border border-gray-100">
                        <p className="leading-relaxed">
                          {product.description || `Varian Bakpia ${product.name} dengan kualitas premium dan rasa yang otentik.`}
                        </p>
                        <p className="mt-2 font-semibold text-[#3E2723]">
                          {t.order.isi}: {product.pieces_per_box || 10} pcs {t.products.perBox.replace('per box (isi 10)', 'per box')}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-32">
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#B22222]/10 sticky top-24 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          <Button size="sm" variant="outline" onClick={() => scrollToCategory('premium')} className="rounded-full border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap">Premium</Button>
          <Button size="sm" variant="outline" onClick={() => scrollToCategory('basah')} className="rounded-full border-green-200 bg-green-50 text-green-700 hover:bg-green-100 whitespace-nowrap">Bakpia Basah</Button>
          <Button size="sm" variant="outline" onClick={() => scrollToCategory('kering')} className="rounded-full border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 whitespace-nowrap">Bakpia Kering</Button>
          <Button size="sm" variant="outline" onClick={() => setIsPricelistOpen(true)} className="rounded-full border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 whitespace-nowrap font-bold">Menu Toko</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#B22222]" size={40} /></div>
        ) : (
          <>
            {renderProductSection('premium', t.order.varianPremium)}
            {renderProductSection('basah', t.order.bakpiaBasah)}
            {renderProductSection('kering', t.order.bakpiaKering)}
          </>
        )}
      </div>

      <Dialog open={isPricelistOpen} onOpenChange={setIsPricelistOpen}>
        <DialogContent className="max-w-3xl p-1 overflow-hidden rounded-2xl bg-black border-none">
          <div className="relative">
            <Button 
               size="icon" 
               variant="ghost" 
               className="absolute top-2 right-2 z-50 bg-black/40 text-white hover:bg-black/60 rounded-full"
               onClick={() => setIsPricelistOpen(false)}
            >
              <Plus className="rotate-45" size={24} />
            </Button>
            <img src={pricelistImg} alt="Pricelist Menu Toko" className="w-full h-auto max-h-[85vh] object-contain rounded-xl" />
          </div>
        </DialogContent>
      </Dialog>

      {getTotalItems() > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 animate-in slide-in-from-bottom duration-300">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-[#3E2723] text-white shadow-2xl border-none rounded-2xl overflow-hidden">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <ShoppingCart size={28} />
                    <span className="absolute -top-2 -right-2 bg-[#B22222] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#3E2723]">
                      {getTotalItems()}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-300">{t.order.totalPembayaran}</p>
                    <p className="text-xl font-bold text-[#FED09D]">Rp {getTotalPrice().toLocaleString()}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setIsCheckoutModalOpen(true)}
                  className="bg-[#B22222] hover:bg-[#8B0000] text-white px-8 py-6 rounded-xl font-bold text-lg"
                >
                  Checkout
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Dialog open={isCheckoutModalOpen} onOpenChange={setIsCheckoutModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <ShoppingCart className="text-[#B22222]" />
              Detail Pesanan
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4 pb-4">
            <div className="space-y-3">
              <h3 className="font-bold text-[#3E2723] flex items-center gap-2"><ClipboardList size={18} /> Ringkasan Keranjang</h3>
              {Object.keys(cart).map((id) => {
                const product = products.find(p => p.id === id);
                if (!product) return null;
                return (
                  <div key={id} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border">
                    <img src={product.image_url ? `${BACKEND_URL}${product.image_url}` : product.image} className="w-12 h-12 object-cover rounded" alt="" />
                    <div className="flex-1">
                      <p className="font-bold text-sm">{product.name}</p>
                      <p className="text-xs text-gray-500">Rp {product.price.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" className="w-7 h-7 rounded-full" onClick={() => handleDecreaseQuantity(id)}><Minus size={14} /></Button>
                      <span className="font-bold text-sm w-4 text-center">{cart[id]}</span>
                      <Button size="icon" variant="outline" className="w-7 h-7 rounded-full" onClick={() => handleIncreaseQuantity(id)}><Plus size={14} /></Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <hr />

            <div className="space-y-4">
              <h3 className="font-bold text-[#3E2723] flex items-center gap-2"><User size={18} /> Data Pengiriman</h3>
              <div className="space-y-2">
                <Label>{t.order.form.nama}</Label>
                <Input value={customerInfo.fullName} onChange={(e) => setCustomerInfo({...customerInfo, fullName: e.target.value})} placeholder={t.order.form.placeholderNama} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.order.form.provinsi}</Label>
                  <Select onValueChange={handleProvinceChange} value={customerInfo.provinceId}>
                    <SelectTrigger>{regionLoading.provinces ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue placeholder={t.order.form.placeholderProvinsi} />}</SelectTrigger>
                    <SelectContent>{provinces.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.order.form.kota}</Label>
                  <Select onValueChange={handleCityChange} value={customerInfo.cityId} disabled={!customerInfo.provinceId}>
                    <SelectTrigger>{regionLoading.cities ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue placeholder={t.order.form.placeholderKota} />}</SelectTrigger>
                    <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.order.form.kecamatan}</Label>
                  <Select onValueChange={handleDistrictChange} value={customerInfo.districtId} disabled={!customerInfo.cityId}>
                    <SelectTrigger>{regionLoading.districts ? <Loader2 className="w-4 h-4 animate-spin" /> : <SelectValue placeholder={t.order.form.placeholderKecamatan} />}</SelectTrigger>
                    <SelectContent>{districts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t.order.form.kodePos}</Label>
                  <Input value={customerInfo.postalCode} onChange={(e) => setCustomerInfo({...customerInfo, postalCode: e.target.value})} placeholder={t.order.form.placeholderKodePos} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t.order.form.alamat}</Label>
                <Input value={customerInfo.addressStreet} onChange={(e) => setCustomerInfo({...customerInfo, addressStreet: e.target.value})} placeholder={t.order.form.placeholderAlamat} />
              </div>

              <div className="space-y-2">
                <Label>{t.order.form.detail}</Label>
                <Textarea value={customerInfo.addressDetails} onChange={(e) => setCustomerInfo({...customerInfo, addressDetails: e.target.value})} placeholder={t.order.form.placeholderDetail} />
              </div>
            </div>

            <div className="bg-[#B22222]/5 p-4 rounded-xl border border-[#B22222]/10 space-y-3">
              <div className="flex items-start space-x-3">
                <Checkbox id="vac" checked={customerInfo.useVacuum} onCheckedChange={(val) => setCustomerInfo({...customerInfo, useVacuum: val})} />
                <div className="grid gap-1 leading-none">
                  <Label htmlFor="vac" className="font-bold cursor-pointer">{t.order.vacuum}</Label>
                  <p className="text-xs text-gray-600">{t.order.vacuumDesc}</p>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-4 space-y-3 border-t border-b">
              <div className="flex justify-between items-center px-1">
                <span className="font-medium text-gray-600">{t.order.totalPembayaran}:</span>
                <span className="text-2xl font-bold text-[#B22222]">Rp {getTotalPrice().toLocaleString()}</span>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={onResetCart}
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50 h-12 font-bold"
                >
                  <Trash2 size={18} className="mr-2" /> Kosongkan
                </Button>
                <Button 
                  onClick={handleSendToWhatsApp} 
                  className="flex-[2] bg-green-600 hover:bg-green-700 text-white h-12 text-lg font-bold"
                >
                  <Send size={18} className="mr-2" /> Pesan via WA
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderPage;
